/**
 * The map of systems, rendered: lanes as tinted rows and flows as orthogonal arrows with haloed labels
 * (SVG), the documents moving along them (SMIL, see glyph.tsx), the data models as HTML buttons laid over
 * the drawing, and a dashed ghost where a flow leaves the map. It fills its container's width and scrolls
 * sideways when the layout is wider, the edges fading softly and the lane labels staying put; a selected
 * or failing node that would be out of view is scrolled into it. Pointing at a node lights its flows and
 * dims the rest; failure beats every fade: a failing node never goes faint with age or as-of, and its red
 * line, red stroke and dropped document keep their full strength while the rest dims. A moving document
 * pauses everything while pointed at and reports its flow (the stage shows a real document). The geometry
 * (boxes, paths, points along them) goes out through `geometryRef` for the story and the ▶ journey.
 */
import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type KeyboardEvent } from 'react';
import type { MapFlow, MapGeometry, MapProps } from './types.ts';
import { LABEL_W, layoutMap, pointAlong, type PlacedNodeX, type RoutedFlowX } from './layout.ts';
import { measure } from './measure.ts';
import { MovingDocs, type Train } from './glyph.tsx';
import { SystemBadge, cx } from '../design/primitives.tsx';
import { stripSystem, systemColor, systemName } from '../lib/systems.ts';
import { age, freshness } from '../lib/freshness.ts';
import { count } from '../lib/format.ts';
import './map.css';

/** How far things fade: with age, past the as-of moment, and when something else is lit; failure never below `failing`. */
const OPACITY = { aging: 0.68, stale: 0.4, past: 0.25, failing: 0.9, unrelatedNode: 0.4, unrelated: 0.18 };
const TONES = ['quiet', 'hover', 'lit', 'fail', 'link'] as const;
/** A lane's tint hue: the system's colour, except that a red-family one (carrier, EDI, CRM) is drawn toward neutral so a lane never reads as an alert. */
function tintOf(system: string): string {
  const c = systemColor(system);
  const m = /^#([0-9a-f]{6})$/i.exec(c);
  if (!m) return c;
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(m[1].slice(i, i + 2), 16) / 255);
  const mx = Math.max(r, g, b);
  const d = mx - Math.min(r, g, b);
  if (d === 0) return c;
  const h = (((mx === r ? ((g - b) / d) % 6 : mx === g ? (b - r) / d + 2 : (r - g) / d + 4) * 60) + 360) % 360;
  return h >= 310 || h <= 32 ? `color-mix(in srgb, ${c} 15%, var(--n6))` : c;
}
/** The soft edge where the map runs on past its box. */
const FADE = 32;

/** How fast the documents move while a line or a document is pointed at: a crawl, still seen to move. */
const SLOW_RATE = 0.06;
/** Seconds to ease down to the crawl, and to speed back up to full pace (gently, ease in and out). */
const SLOW_DOWN_S = 0.35;
const SPEED_UP_S = 0.7;
/** How near a moving document the pointer slows them (px). */
const NEAR_PX = 28;

export function Map({
  nodes, flows, links, layout: layoutOptions, selection, highlight, asOf, now: nowProp, motion = 'live', speed = 1, glyphScale = 1, maxGlyphs = 2,
  onNode, onFlow, onLane, onGlyphHover, geometryRef, className, cue, laneLabels, pace: paceMode = 'loop', departures,
}: MapProps) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const scroller = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLDivElement>(null);
  const inner = useRef<HTMLDivElement>(null);
  const svg = useRef<SVGSVGElement>(null);
  const [width, setWidth] = useState<number>(layoutOptions?.width ?? 1200);
  const [fontsReady, setFontsReady] = useState(false);
  const [hoverNode, setHoverNode] = useState<string | null>(null);
  const [hoverFlow, setHoverFlow] = useState<string | null>(null);
  const [glyphHeld, setGlyphHeld] = useState(false);
  // The flow of the document last pointed at (its card may still be open over the map)
  const heldFlow = useRef<string | null>(null);
  const now = nowProp ?? Date.now();
  const reduced = useMemo(() => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches, []);

  useEffect(() => {
    const el = scroller.current;
    if (!el || layoutOptions?.width) return;
    const ro = new ResizeObserver(([e]) => setWidth(Math.max(320, Math.floor(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, [layoutOptions?.width]);
  useEffect(() => {
    document.fonts?.ready.then(() => setFontsReady(true)).catch(() => undefined);
  }, []);

  // A node without a worded sub-line gets one from its count and age ("24 today · 12 min"): a count never
  // disappears because a page left the detail out
  const worded = useMemo(() => nodes.map((n) => (n.detail || n.count === null || n.count === undefined
    ? n
    : { ...n, detail: `${count(n.count)} ${n.countLabel}${n.last ? ` · ${age(n.last, now)} ago` : ''}` })), [nodes, now]);
  const lay = useMemo(
    () => layoutMap(worded, flows, links ?? [], { ...layoutOptions, width: layoutOptions?.width ?? width, measure, glyphScale }),
    // fontsReady re-measures once the real fonts are in
    [worded, flows, links, layoutOptions, width, fontsReady, glyphScale], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const maxRuns = Math.max(1, ...lay.flows.map((f) => f.runs));
  const byId = useMemo(() => new window.Map(lay.nodes.map((n) => [n.id, n])), [lay]);
  // The nodes a failing flow leaves or reaches: they carry the failure too
  const failingEnds = useMemo(() => {
    const s = new Set<string>();
    for (const f of lay.flows) {
      if (f.failed <= 0) continue;
      s.add(f.from);
      if (f.to) s.add(f.to);
    }
    return s;
  }, [lay]);

  // How many documents each flow shows (1..maxGlyphs by its share of the runs, never more than it ran; one
  // train when it fans out or folds, the fan being the extra), and when: the flows sharing a column gap take
  // one lap time and spread their documents over it, so each is mid-gap at its own moment and they trickle
  // through the gap one after another instead of piling up
  // Each flow's documents run on a clock of their own (an <svg> of their own), so pointing at a line slows its documents
  // and no others
  const flowSvgs = useRef(new window.Map<string, SVGSVGElement>());
  const departed = useRef(new window.Map<string, number>());
  const trains = useMemo(() => {
    const cap = Math.max(1, Math.floor(maxGlyphs));
    const groups = new window.Map<number, { f: RoutedFlowX; n: number; mid: number }[]>();
    for (const f of lay.flows) {
      const fanned = f.fans.length > 0 && !!(f.split ?? f.merge);
      const n = fanned ? 1 : Math.max(1, Math.min(cap, f.runs, 1 + Math.round((cap - 1) * (f.runs / maxRuns))));
      const mid = (f.gapWindow[0] + f.gapWindow[1]) / 2;
      groups.set(f.gapAt, [...(groups.get(f.gapAt) ?? []), { f, n, mid }]);
    }
    const out = new window.Map<string, Train[]>();
    const rate = speed > 0 ? speed : 1;
    for (const members of groups.values()) {
      members.sort((a, b) => a.f.route.pts[0].y - b.f.route.pts[0].y || a.f.id.localeCompare(b.f.id));
      // A crowded gap (a hub's corridor) shows one document per flow, on a longer lap, so they trickle through
      // one after another instead of piling up where the arrows meet
      if (members.length > 2) for (const m of members) m.n = 1;
      const total = members.reduce((acc, m) => acc + m.n, 0);
      const longest = Math.max(...members.map((m) => m.f.length));
      const crowd = 1 + 0.12 * Math.max(0, members.length - 2);
      const dur = Math.min(8, Math.max(3.4, 2.4 + longest / 260) * crowd) / rate;
      // Slots round-robin, so a flow's own documents are as far apart as the group allows
      let slot = 0;
      for (let k = 0; k < cap; k++) {
        for (const m of members) {
          if (k >= m.n) continue;
          const phase = (((m.mid - slot / total) % 1) + 1) % 1;
          out.set(m.f.id, [...(out.get(m.f.id) ?? []), { dur, begin: -phase * dur }]);
          slot++;
        }
      }
    }
    if (paceMode === 'rhythm') {
      // A lap, then a pause as long as the integration usually waits, log-scaled: every minute none, every 15 min ~6 s,
      // hourly ~9 s, daily ~15 s
      const day = 86_400_000;
      for (const f of lay.flows) {
        const every = f.rhythm?.everyMs ?? (f.runs > 0 ? day / f.runs : day);
        const pause = Math.max(0, Math.min(18, 1.5 * Math.log2(Math.max(1, every / 60_000))));
        out.set(f.id, (out.get(f.id) ?? []).slice(0, 1).map((tr) => ({ ...tr, pause })));
      }
    } else if (paceMode === 'live') {
      // A lap per run that just happened, from the moment of this flow's own clock it came in at
      const now = Date.now();
      for (const f of lay.flows) {
        const dur = out.get(f.id)?.[0]?.dur ?? 4;
        const el = flowSvgs.current.get(f.id);
        const deps = (departures?.get(f.id) ?? []).map((d) => {
          let at = departed.current.get(d.key);
          if (at === undefined) {
            at = (el?.getCurrentTime() ?? 0) + Math.max(0.05, (d.at - now) / 1000);
            departed.current.set(d.key, at);
          }
          return { dur, begin: 0, at, key: d.key };
        });
        out.set(f.id, deps);
      }
    }
    return out;
  }, [lay, maxGlyphs, maxRuns, speed, paceMode, departures]);

  // What is lit: the highlight given, else the node pointed at with its flows and their other ends
  const lit = useMemo(() => {
    const src = highlight && ((highlight.nodes?.length ?? 0) > 0 || (highlight.flows?.length ?? 0) > 0) ? highlight : hoverNode ? { nodes: [hoverNode] } : null;
    if (!src) return null;
    const ns = new Set(src.nodes ?? []);
    const fs = new Set<string>();
    for (const f of lay.flows) {
      if (src.flows?.includes(f.id) || src.flows?.includes(f.process)) {
        fs.add(f.id);
        ns.add(f.from);
        if (f.to) ns.add(f.to);
      }
    }
    for (const f of lay.flows) {
      if (hoverNode && !highlight && (f.from === hoverNode || f.to === hoverNode)) {
        fs.add(f.id);
        ns.add(f.from);
        if (f.to) ns.add(f.to);
      } else if (ns.has(f.from) && (!f.to || ns.has(f.to))) fs.add(f.id);
    }
    return { ns, fs };
  }, [highlight, hoverNode, lay]);

  // How faint a node's body is: with age and past the as-of moment, never under .9 when it fails or a
  // failing flow leaves or reaches it; then dimmed when something else is lit (its red line stays full)
  const nodeDim = (n: PlacedNodeX) => {
    let o = 1;
    const fr = freshness(n.last, n.usual, now);
    if (fr === 'aging') o = OPACITY.aging;
    if (fr === 'stale') o = OPACITY.stale;
    if (asOf != null && n.last != null && n.last > asOf) o = Math.min(o, OPACITY.past);
    if ((n.failed ?? 0) > 0 || failingEnds.has(n.id)) o = Math.max(o, OPACITY.failing);
    if (lit && !lit.ns.has(n.id)) o = Math.min(o, OPACITY.unrelatedNode);
    return o;
  };
  // A failing flow keeps its red stroke, label and dropping document at full strength whatever is lit
  // A bundle's pill speaks for all its flows: it stays when any of them is lit
  const litFlow = (f: RoutedFlowX) => !!lit && (lit.fs.has(f.id) || (!!f.bundle && lay.flows.some((m) => m.to === f.to && lit.fs.has(m.id))));
  const flowOpacity = (f: RoutedFlowX) => (f.failed > 0 ? 1 : lit && !litFlow(f) ? OPACITY.unrelated : 1);
  const flowFaded = (f: RoutedFlowX) => {
    if (f.failed > 0) return false;
    const target = f.to ? byId.get(f.to) : null;
    return asOf != null && !!target?.last && target.last > asOf;
  };

  // The moving documents stop when asked to and at speed 0. While a line, a node or a document's card is pointed at, or
  // the pointer is near a moving document (it may slide under a pointer held still, which fires no pointer event), they
  // slow right down to SLOW_RATE:
  // they still move, so the map is seen to be live, but a document is easy to look at. The pace eases down quickly and
  // speeds back up gently (ease in and out), never at once. The SVG's clock is paused and stepped by hand meanwhile, so
  // nothing jumps when the pace changes.
  const pointer = useRef<{ x: number; y: number } | null>(null);
  // What is pointed at, for the flows to slow: a line, a document's flow (held, or its card), a node's flows
  const pointed = useRef<{ flow: string | null; glyph: string | null; node: string | null }>({ flow: null, glyph: null, node: null });
  pointed.current = { flow: hoverFlow, glyph: glyphHeld ? heldFlow.current : null, node: hoverNode };
  // Each flow's pace (1 = full, 0 = the crawl) outlives a new layout or a re-render: a card opening does not reset it
  const paces = useRef(new window.Map<string, number>());
  const flowEnds = useRef(new window.Map<string, [string, string | null]>());
  flowEnds.current = new window.Map(lay.flows.map((f) => [f.id, [f.from, f.to]]));
  useEffect(() => {
    const svgs = flowSvgs.current;
    if (motion === 'paused' || speed === 0) {
      for (const el of svgs.values()) el.pauseAnimations();
      return;
    }
    // The pointer anywhere on the page: over the map, over a document's card, over a node
    const move = (e: PointerEvent) => { pointer.current = { x: e.clientX, y: e.clientY }; };
    window.addEventListener('pointermove', move, { passive: true });
    // The flows to slow: the line pointed at, the flow of a document held or near the pointer (it may slide under a
    // pointer held still, which fires no pointer event) or of the card it opened, the flows of a node pointed at;
    // checked a few times a second, not every frame
    let slowSet = new Set<string>();
    let lastCheck = 0;
    const slowFlows = (t: number) => {
      if (t - lastCheck < 90) return slowSet;
      lastCheck = t;
      const next = new Set<string>();
      const p = pointed.current;
      if (p.flow) next.add(p.flow);
      if (p.glyph) next.add(p.glyph);
      if (p.node) for (const [id, [from, to]] of flowEnds.current) if (from === p.node || to === p.node) next.add(id);
      const at = pointer.current;
      if (at) {
        const over = document.elementFromPoint(at.x, at.y);
        if (over?.closest('[data-hold-map]') && heldFlow.current) next.add(heldFlow.current);
        for (const [id, el] of svgs) {
          for (const d of el.querySelectorAll<SVGGElement>('.mvm-doc')) {
            const r = d.getBoundingClientRect();
            if (r.width === 0 || at.x < r.left - NEAR_PX || at.x > r.right + NEAR_PX || at.y < r.top - NEAR_PX || at.y > r.bottom + NEAR_PX) continue;
            if (Number(getComputedStyle(d).opacity) < 0.2) continue;
            next.add(id);
            break;
          }
        }
      }
      slowSet = next;
      return slowSet;
    };
    const manual = new Set<string>();
    let frameId = 0;
    let last = performance.now();
    const ease = (x: number) => (x < 0.5 ? 2 * x * x : 1 - 2 * (1 - x) * (1 - x));
    const tick = (t: number) => {
      const dt = Math.min(0.1, (t - last) / 1000);
      last = t;
      const slow = slowFlows(t);
      for (const [id, el] of svgs) {
        const was = paces.current.get(id) ?? 1;
        const p = slow.has(id) ? Math.max(0, was - dt / SLOW_DOWN_S) : Math.min(1, was + dt / SPEED_UP_S);
        paces.current.set(id, p);
        if (p < 1) {
          if (!manual.has(id)) { el.pauseAnimations(); manual.add(id); }
          el.setCurrentTime(el.getCurrentTime() + dt * (SLOW_RATE + (1 - SLOW_RATE) * ease(p)));
        } else if (manual.has(id)) {
          el.unpauseAnimations();
          manual.delete(id);
        }
      }
      frameId = requestAnimationFrame(tick);
    };
    for (const [id, el] of svgs) if ((paces.current.get(id) ?? 1) >= 1) el.unpauseAnimations();
    frameId = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('pointermove', move);
    };
  }, [motion, speed, lay]);
  // A held glyph may be remounted or laid out anew before its pointerleave comes: the hold ends with the layout,
  // and the moment the pointer moves anywhere that is not a document
  useEffect(() => setGlyphHeld(false), [lay, motion, speed]);
  useEffect(() => {
    if (!glyphHeld) return;
    const release = (e: PointerEvent) => {
      if (e.target instanceof Element && e.target.closest('.mvm-doc')) return;
      setGlyphHeld(false);
      onGlyphHover?.(null, null);
    };
    document.addEventListener('pointermove', release);
    return () => document.removeEventListener('pointermove', release);
  }, [glyphHeld, onGlyphHover]);

  // Where the map runs on past its box, its edge fades (the lane labels keep a little glass once nodes slide
  // under them); measured after every layout and scroll
  const edges = useCallback(() => {
    const el = scroller.current;
    const fr = frame.current;
    if (!el || !fr) return;
    const pr = parseFloat(getComputedStyle(el).paddingRight) || 0;
    const max = el.scrollWidth - el.clientWidth;
    const l = el.scrollLeft > 1;
    const r = el.scrollLeft < max - 1;
    el.style.setProperty('--mvm-pr', `${pr}px`);
    const fade = `${l ? 'l' : ''}${r ? 'r' : ''}`;
    if (fade) el.dataset.fade = fade;
    else delete el.dataset.fade;
    if (l) fr.dataset.scrolled = '';
    else delete fr.dataset.scrolled;
  }, []);
  useLayoutEffect(edges, [lay, edges]);
  // When the map is wider than its box: the selected node comes into view; without one the map stays at its start
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el || el.scrollWidth <= el.clientWidth + 1) return;
    const pr = parseFloat(getComputedStyle(el).paddingRight) || 0;
    const view = el.clientWidth - pr;
    const max = el.scrollWidth - el.clientWidth;
    const clamp = (v: number) => Math.max(0, Math.min(max, v));
    const shows = (left: number, b: { x: number; w: number }) => b.x >= left + LABEL_W - 1 && b.x + b.w <= left + view + 1;
    let left: number | null = null;
    const picked = selection ? byId.get(selection) : undefined;
    if (picked) {
      if (shows(el.scrollLeft, picked)) return;
      left = clamp(picked.x + picked.w / 2 - LABEL_W - (view - LABEL_W) / 2);
    } else {
      // Nothing selected: the map rests at its start, the first column right of the lane labels. (It used to
      // jump to the view showing most failures, which hid the first column under the labels.)
      return;
    }
    el.scrollTo({ left, behavior: reduced ? 'auto' : 'smooth' });
  }, [lay, selection, byId, reduced]);

  useLayoutEffect(() => {
    if (!geometryRef) return;
    const geometry: MapGeometry = {
      box: (id) => {
        const n = byId.get(id);
        return n ? { x: n.x, y: n.y, w: n.w, h: n.h } : null;
      },
      path: (flowId) => {
        const f = lay.flows.find((x) => x.id === flowId);
        if (!f) return null;
        const pointAt = (t: number) => {
          const p = pointAlong(f.route, t);
          return { x: p.x, y: p.y, angle: (p.angle * 180) / Math.PI };
        };
        return { d: f.d, length: f.length, pointAt, mid: pointAt(0.5) };
      },
      flowBetween: (from, to, process) =>
        lay.flows.find((f) => f.from === from && (f.to ?? null) === to && (!process || f.process === process))
        ?? lay.flows.find((f) => f.from === from && (f.to ?? null) === to)
        ?? null,
      origin: () => inner.current?.getBoundingClientRect() ?? null,
    };
    geometryRef(geometry);
    return () => geometryRef(null);
  }, [geometryRef, lay, byId]);

  const glyphS = lay.glyphS;
  const pathId = (i: number) => `${uid}-p${i}`;
  const fanId = (i: number, k: number) => `${uid}-p${i}-f${k}`;
  const toneOf = (f: RoutedFlowX) => (f.failed > 0 ? 'fail' : lit?.fs.has(f.id) && (highlight || hoverNode) ? 'lit' : hoverFlow === f.id ? 'hover' : 'quiet');
  const keyActivate = (f: MapFlow) => (e: KeyboardEvent<SVGPathElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onFlow?.(f);
    }
  };
  const hoverGlyph = (flow: MapFlow | null, at: { x: number; y: number } | null) => {
    if (flow) heldFlow.current = flow.id;
    setGlyphHeld(!!flow);
    onGlyphHover?.(flow, at);
  };
  const flowById = (id: string) => lay.flows.find((f) => f.id === id);

  return (
    <div ref={frame} className={cx('mvm-frame relative', className)} data-cue={cue && !reduced ? '' : undefined} style={{ '--mvm-l0': `${LABEL_W}px`, '--mvm-fade': `${FADE}px` } as CSSProperties}>
      <div ref={scroller} className="mvm-map relative overflow-x-auto overflow-y-hidden" onScroll={edges}>
        <div ref={inner} className="relative" style={{ width: lay.width, height: lay.height }}>
          <svg ref={svg} className="absolute inset-0 block" width={lay.width} height={lay.height} aria-hidden="true">
            <defs>
              {TONES.map((tone) => (
                <marker key={tone} id={`${uid}-ah-${tone}`} viewBox="0 0 10 10" refX="8.5" refY="5" markerWidth="9" markerHeight="9" markerUnits="userSpaceOnUse" orient="auto">
                  <path className={cx('mvm-arrow', tone !== 'quiet' && tone)} d="M1.5,1.5 L8.5,5 L1.5,8.5" />
                </marker>
              ))}
            </defs>
            {lay.lanes.map((l) => (
              <rect key={l.system} x={0} y={l.y} width={lay.width} height={l.height}
                style={{ fill: `color-mix(in srgb, ${tintOf(l.system)} var(--tint), transparent)` }} />
            ))}
            {lay.links.map((l) => (
              <path key={`${l.from}→${l.to}`} className="mvm-link" d={l.d} markerEnd={`url(#${uid}-ah-link)`}
                style={{ opacity: lit && !(lit.ns.has(l.from) && lit.ns.has(l.to)) ? OPACITY.unrelated : 1 }} />
            ))}
            {lay.flows.map((f, i) => {
              const tone = toneOf(f);
              const w = f.failed > 0 ? 1.5 : 1.5 + 1.5 * Math.sqrt(f.runs / maxRuns);
              const splitting = !!f.split && f.fans.length > 0;
              const merging = !!f.merge && f.fans.length > 0;
              const upTo = `${f.length * f.swapAt} ${f.length}`;
              const from = `0 ${f.length * f.swapAt} ${f.length}`;
              return (
                <g key={f.id} className={cx('mvm-flow', f.failed > 0 && 'fail', tone === 'lit' && 'lit', hoverFlow === f.id && 'hover')} style={{ opacity: flowOpacity(f) }}>
                  {f.fans.map((fan, k) => (
                    <path key={k} id={fanId(i, k)} className="mvm-fan" d={fan.d}
                      strokeDasharray={splitting ? `0 ${fan.length * fan.swapAt} ${fan.length}` : `${fan.length * fan.swapAt} ${fan.length}`}
                      markerEnd={splitting ? `url(#${uid}-ah-${tone})` : undefined} />
                  ))}
                  <path id={pathId(i)} className="mvm-line" d={f.d} strokeWidth={w}
                    strokeDasharray={splitting ? upTo : merging ? from : undefined}
                    markerEnd={splitting ? undefined : `url(#${uid}-ah-${tone})`} />
                  <path className="mvm-hit" d={f.d} role="button" tabIndex={0} aria-label={`${f.labelText} · ${stripSystem(f.process)}`}
                    onClick={() => onFlow?.(f)} onKeyDown={keyActivate(f)}
                    onPointerEnter={() => setHoverFlow(f.id)} onPointerLeave={() => setHoverFlow(null)} />
                </g>
              );
            })}
            {/* A document passing under a label pill fades out around it (a soft cut-out in a mask) instead of showing half of itself */}
            <defs>
              <filter id={`${uid}-pillsoft`} x="-20%" y="-20%" width="140%" height="140%"><feGaussianBlur stdDeviation="2.5" /></filter>
              <mask id={`${uid}-pillmask`} maskUnits="userSpaceOnUse" x={0} y={0} width={lay.width} height={lay.height}>
                <rect x={0} y={0} width={lay.width} height={lay.height} fill="white" />
                <g filter={`url(#${uid}-pillsoft)`}>
                  {lay.flows.filter((f) => !f.labelHidden).map((f) => <rect key={f.id} x={f.labelAt.x - 3} y={f.labelAt.y - 13} width={f.labelW + 6} height={26} rx={13} fill="black" />)}
                </g>
              </mask>
            </defs>
            {lay.flows.filter((f) => !f.labelHidden).map((f) => (
              <g key={f.id} style={{ opacity: flowOpacity(f) }} className={cx('mvm-labelled', f.failed > 0 && 'fail')} data-bundle={f.bundle ? f.bundle.n : undefined}
                onClick={() => onFlow?.(f)} onPointerEnter={() => setHoverFlow(f.id)} onPointerLeave={() => setHoverFlow(null)}>
                {f.leader && <line className="mvm-leader" x1={f.leader.x1} y1={f.leader.y1} x2={f.leader.x2} y2={f.leader.y2} />}
                <rect className="mvm-pill" x={f.labelAt.x} y={f.labelAt.y - 7} width={f.labelW} height={14} rx={7} />
                <text className={cx('mvm-label', f.failed > 0 && 'fail')} x={f.labelAt.x + f.labelW / 2} y={f.labelAt.y + 3.8} textAnchor="middle">
                  {f.labelText}
                </text>
              </g>
            ))}
          </svg>

          {/* Each flow's documents in an <svg> of their own: their own clock, slowed alone when pointed at */}
          {lay.flows.map((f, i) => (
            <svg key={`docs-${f.id}`} className="absolute inset-0 block" width={lay.width} height={lay.height} aria-hidden="true" data-flow-docs={f.id}
              ref={(el) => { if (el) flowSvgs.current.set(f.id, el); else flowSvgs.current.delete(f.id); }}>
              <g mask={`url(#${uid}-pillmask)`} style={{ opacity: flowOpacity(f) }}>
                <MovingDocs flow={f} pathId={pathId(i)} fanIds={f.fans.map((_, k) => fanId(i, k))} scale={glyphS} speed={speed} motion={motion}
                  reduced={reduced} trains={trains.get(f.id) ?? []} faded={flowFaded(f)} quiet={f.quiet} live={paceMode === 'live'} onHover={hoverGlyph} />
              </g>
            </svg>
          ))}

          {lay.ghosts.map((g) => {
            const own = g.flowIds.map(flowById).filter((f): f is RoutedFlowX => !!f);
            const litUp = own.some((f) => toneOf(f) === 'lit' || hoverFlow === f.id);
            const opacity = Math.max(...own.map(flowOpacity));
            return (
              <div key={g.id} className={cx('mvm-ghost', g.failed && 'fail', litUp && 'lit')}
                style={{ left: g.x, top: g.y, width: g.w, height: g.h, opacity }}
                onPointerEnter={() => setHoverFlow(g.flowIds[0] ?? null)} onPointerLeave={() => setHoverFlow(null)}>
                <span className="mvm-ghost-label">
                  {g.labelLines.map((line, i) => <span key={i} className="block">{line}</span>)}
                </span>
                {g.fact && <span className="mvm-node-detail">{g.fact}</span>}
              </div>
            );
          })}

          {lay.nodes.map((n) => {
            const selected = selection === n.id;
            const failLine = n.failText && (n.failOwnLine || !n.detail);
            return (
              <button key={n.id} type="button" data-node={n.id} aria-pressed={selected}
                aria-label={[n.label, n.detail, n.failText].filter(Boolean).join(', ')}
                className={cx('mvm-node', selected && 'selected', n.absent && 'absent', (n.failed ?? 0) > 0 && 'failing')}
                style={{ left: n.x, top: n.y, width: n.w, height: n.h, '--mvm-dim': nodeDim(n) } as CSSProperties}
                onClick={() => onNode?.(n.id)} onPointerEnter={() => setHoverNode(n.id)} onPointerLeave={() => setHoverNode(null)}>
                <span className="mvm-node-label" style={{ fontSize: n.labelSize, lineHeight: n.labelSize === 15 ? '18px' : '16px' }}>
                  {n.labelLines.map((line, i) => <span key={i} className="block">{line}</span>)}
                </span>
                {n.detailLines.map((line, i) => {
                  const joined = i === n.detailLines.length - 1 && n.failText && !n.failOwnLine;
                  return (
                    <span key={i} className="mvm-node-detail">
                      <span className="mvm-dim">{line}{joined && ' · '}</span>
                      {joined && <span className="fail">{n.failText}</span>}
                    </span>
                  );
                })}
                {failLine && n.failLines.map((line, i) => <span key={i} className="mvm-node-detail fail">{line}</span>)}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mvm-lanes absolute left-0 top-0 z-[1] pointer-events-none" style={{ width: LABEL_W, height: lay.height }}>
        {lay.lanes.map((l) => (
          <div key={l.system} className="absolute left-0 flex flex-col justify-center items-start gap-0.5 pl-4 pr-2" style={{ top: l.y, height: l.height, width: LABEL_W }}>
            {laneLabels?.get(l.system) ? (
              // A family's lane: the systems it stands for, stacked, and how many
              <>
                <span className="flex items-center -space-x-1.5" data-family-lane={l.system}>
                  {laneLabels.get(l.system)!.systems.slice(0, 4).map((s) => <SystemBadge key={s} code={s} size={20} />)}
                </span>
                <span className="text-small leading-[14px] font-medium text-n8 max-w-full">{laneLabels.get(l.system)!.label}</span>
              </>
            ) : onLane ? (
              // The lane's system opens (Now: its peek); the same look, a quiet hover
              <button type="button" className="mvm-lane-btn" data-lane={l.system} aria-label={`System ${systemName(l.system)}`} onClick={() => onLane(l.system)}>
                <SystemBadge code={l.system} size={l.height >= 72 ? 24 : 20} />
                <span className="text-small leading-[14px] font-medium text-n8 max-w-full">{systemName(l.system)}</span>
              </button>
            ) : (
              <>
                <SystemBadge code={l.system} size={l.height >= 72 ? 24 : 20} />
                <span className="text-small leading-[14px] font-medium text-n8 max-w-full">{systemName(l.system)}</span>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** The same component under a name that does not shadow the global `Map` in modules that also need one. */
export const SystemsMap = Map;
